import { useCallback } from 'react';
import { Cart } from '@shared/types/cart/Cart';
import { sdk } from '@/sdk';
import useSWR, { useSWRConfig } from 'swr';
import { Address } from '@shared/types/account/Address';
import { Order } from '@shared/types/cart';
import { calculateTransaction } from '@/lib/utils/calculate-transaction';
import { MutationLockOptions, useMutationLock } from '@/providers/mutation-lock';
import { revalidateStockForSkus } from '@/utils/server/revalidate-stock';
import {
  pushBrazeAddToCart,
  pushBrazeRemoveFromCart,
  mapLineItemsToBrazeProducts,
  buildBrazeAddedProducts,
} from '@/lib/analytics/dataLayer';
import { useTranslations } from 'use-intl';
import toast from '@/components/atoms/toaster/helpers/toast';
import { CartWithTransaction, CheckoutPayload, ClearCartResult, QuoteRequestPayload } from './types';

const useCart = (businessUnitKey?: string, storeKey?: string) => {
  const { mutate: mutateGlobal } = useSWRConfig();
  const translate = useTranslations();

  // EWM-612 — cart-content writes default to the 'cart' scope, which drives the
  // <CartProcessingModal> (instead of disabling individual controls). Background
  // recomputes (applyFreight) and terminal flows (checkout / requestQuote) pass
  // scope:'global' to opt OUT of the modal — they own their own UX and shouldn't
  // flash "Updating your cart…" on checkout load. Quantity edits don't lock at all
  // (EWM-610 — optimistic + debounced).
  const { withLock: lock } = useMutationLock('cart');
  const withLock = useCallback(
    function withCartLock<T>(fn: () => Promise<T>, opts?: MutationLockOptions): Promise<T> {
      return lock(fn, { scope: 'cart', ...opts });
    },
    [lock],
  );

  const getCart = useCallback(async () => {
    const result = await sdk.composableCommerce.cart.getCart({
      businessUnitKey: businessUnitKey as string,
      storeKey: storeKey as string,
    });

    return result.isError ? ({} as Cart) : result.data;
  }, [businessUnitKey, storeKey]);

  const { data, mutate, ...response } = useSWR(
    businessUnitKey && storeKey ? `/action/cart/getCart/${businessUnitKey}/${storeKey}` : null,
    getCart,
    // EWM-476 — the global SWRProvider sets revalidateOnFocus + revalidateIfStale to
    // false. The cart MUST re-check the server when the customer returns to the tab
    // or re-opens the cart/checkout, so an out-of-band change (async FPRN freeze, or
    // a mutation/order on the same shared server cart from another tab/device) is
    // reflected without needing a logout/login. Opt the cart back in to focus + stale
    // revalidation, overriding the global default.
    { revalidateOnFocus: true, revalidateIfStale: true },
  );

  const mutateAll = useCallback(() => {
    // EWM-476 — the cart SWR key is a STRING (`/action/cart/getCart/{bu}/{store}`),
    // not an array. The previous matcher read `key[0]` (the first character, '/')
    // so it never matched and `mutateAll` silently no-op'd — callers (e.g. the cart
    // client wrapper) thought they were refreshing the cart but weren't. Match the
    // string key directly so every cart cache entry is revalidated.
    mutateGlobal((key) => typeof key === 'string' && key.startsWith('/action/cart/getCart'), undefined, {
      revalidate: true,
    });
  }, [mutateGlobal]);

  // EWM-714 — read the LATEST cart from the server and hand it back.
  //
  // Purely additive, and deliberately a READ: it calls `cart/getCart`, so it
  // neither takes the mutation lock nor changes anything in commercetools.
  // Both existing refresh paths are unusable for a caller that needs the live
  // contents *now*:
  //   • `mutateAll` revalidates but returns nothing, so the caller can only read
  //     the next render's closure — which is precisely the value the checkout
  //     stale-cart gate must not trust (a focus revalidation can have already
  //     refreshed the render, hiding the very change being looked for).
  //   • `recalculate` returns a cart but is a WRITE (`cart/recalculate`) under
  //     the lock, and can itself move the cart it is being asked about.
  //
  // It reads through the SDK rather than revalidating the shared SWR key
  // (`mutate()` with no data, which is what round 0 did). Review R1-1/R2-2: that
  // shape fails UNSAFELY, silently, and in two different ways, both measured on
  // dev — because a no-data `mutate()` answers from the CACHE, not from the read
  // it triggers. SWR 2.4.2's `internalMutate` takes its no-data branch
  // (`args.length < 3`) and returns `startRevalidate()`, which is
  // `revalidators[0](MUTATE_EVENT).then(() => get().data)`
  // (`swr/dist/_internal/config-context-12s-*.mjs:271`, no-data branch `:277-279`):
  // the caller is handed whatever the cache holds once the revalidation is over,
  // never the revalidation's own result.
  //
  // Note there is only ONE failure shape here, not two: the SDK does NOT reject.
  // `callAction` catches a thrown fetch and returns `handleError(...)` — which
  // returns `{ isError: true, tracing, error }` — and returns it again when the
  // response itself is an error (`@commercetools/frontend-sdk/lib/index.js:1050`
  // `callAction`, `:1087-1105` the two handled paths, `:1022-1026` the return).
  // So a `try/catch` around the read is a backstop, not the live path. What
  // decides which of the two outcomes below you get is a race in SWR:
  //   • The failed read LANDS in the cache. The SWR fetcher maps `isError` to
  //     `{}` (`getCart` above), so it REPLACES the rendered cart with an empty
  //     one and CheckoutV2's empty-cart guard bounces the customer to /cart —
  //     payment method and PO lost, no message (review R1-1: skeleton at 568 ms,
  //     zero alerts, final URL /cart). A transient blip must not cost the page.
  //   • The failed read does NOT land, because SWR discards a response that a
  //     later request or an overlapping mutation has overtaken — both branches
  //     return before `finalState.data` is ever assigned (`swr/dist/index/index.mjs:423-429`
  //     and `:445-456`, vs the assignment at `:463`). `get().data` is then still
  //     the STALE cart, whose signature equals the pinned one, so the gate waves
  //     the order through. Measured with an injected 500 on the click-time read:
  //     the network trace ran getCart 500 → updateCart → cart/checkout → an
  //     ORDER. The gate failed OPEN, which is the defect it exists to stop.
  // Reading through the SDK makes both unrepresentable rather than unlikely: the
  // failure arrives as a value this function inspects, and the cache is written
  // only when a real cart came back.
  //
  // So: an unreadable cart returns `undefined` and the cache is NEVER touched
  // (callers must fail closed on that rather than assume "unchanged"); a
  // readable one is seeded into the cache with `revalidate: false` so the page
  // repaints with exactly what the caller was answered (EWM-714 AC-2), and is
  // handed back shape-identical to `cart` below (same computed `transaction`).
  const refreshCart = useCallback(async (): Promise<CartWithTransaction | undefined> => {
    if (!businessUnitKey || !storeKey) return undefined;

    const result = await sdk.composableCommerce.cart.getCart({ businessUnitKey, storeKey });
    // `isError` is the BFF/network failure; a success with no `cartId` is the
    // same `{}` non-cart — neither is evidence about the customer's cart, and
    // neither may reach the cache.
    if (result.isError || !result.data?.cartId) return undefined;

    const fresh = result.data;
    await mutate(fresh, { revalidate: false });
    return { ...fresh, transaction: calculateTransaction(fresh) };
  }, [businessUnitKey, storeKey, mutate]);

  // EWM-740 — the BFF auto-removes a discount code that stops qualifying after a
  // cart edit (e.g. quantity dropped below the promo threshold), so checkout can't
  // fail on it. The code would otherwise vanish silently — tell the customer.
  // Fires only when a code present before the edit is gone after it; the manual
  // removeDiscount path doesn't call this, so it never false-positives.
  const notifyIfDiscountAutoRemoved = useCallback(
    (before?: Cart, after?: Partial<Cart>) => {
      const afterCodes = after?.discountCodes ?? [];
      const autoRemoved = (before?.discountCodes ?? []).some(
        (prev) => !afterCodes.some((next) => next.discountCodeId === prev.discountCodeId),
      );
      if (autoRemoved) {
        toast.warning(translate('cart.discount-removed-no-longer-qualifies'));
      }
    },
    [translate],
  );

  const isLoading = !(businessUnitKey && storeKey) || response.isLoading;

  const isQuotationCart = data?.origin === 'Quote';

  const addItem = useCallback(
    (lineItems: Array<{ sku: string; count: number; algoliaQueryId?: string; algoliaIndex?: string }>) =>
      withLock(async () => {
        const payload = {
          lineItems: lineItems.map(({ sku, count, algoliaQueryId, algoliaIndex }) => ({
            variant: { sku },
            count,
            ...(algoliaQueryId ? { algoliaQueryId } : {}),
            ...(algoliaIndex ? { algoliaIndex } : {}),
          })),
          businessUnitKey,
        };

        const result = await sdk.composableCommerce.cart.addItem(payload, {
          businessUnitKey: businessUnitKey as string,
          storeKey: storeKey as string,
        });

        if (!result.isError) {
          mutate(result.data, { revalidate: false });
          // EWM-583 — mirror retail: forward the add to Braze (via GTM). cart_items is
          // the resulting cart; items_added is this action's delta (added counts).
          const cartLineItems = result.data?.lineItems ?? [];
          pushBrazeAddToCart({
            currency: result.data?.sum?.currencyCode ?? '',
            cart_items: mapLineItemsToBrazeProducts(cartLineItems),
            items_added: buildBrazeAddedProducts(cartLineItems, lineItems),
          });
        }

        return result.isError
          ? {
              success: false,
              message: result.error.message,
              errorName: (result.error as { errorName?: string }).errorName,
              statusCode: (result.error as { statusCode?: number }).statusCode,
            }
          : { ...result.data, success: true };
      }),
    [withLock, mutate, businessUnitKey, storeKey],
  );

  // EWM-247 (QUICK-01) — canonical batch add for the Quick Order drawers. Unlike
  // the general `addItem` (a single atomic cart update), this hits the dedicated
  // `quickOrder/addItems` BFF action, which adds the purchasable rows and returns
  // the rest in `failed[]` so the drawer can flag them inline instead of the
  // whole batch failing on one bad SKU.
  const quickOrderAddItems = useCallback(
    (lineItems: Array<{ sku: string; count: number }>) =>
      withLock(async () => {
        const result = await sdk.composableCommerce.cart.quickOrderAddItems(
          { items: lineItems },
          { businessUnitKey: businessUnitKey as string, storeKey: storeKey as string },
        );

        if (result.isError) {
          return {
            success: false,
            failed: [],
            message: result.error.message,
            errorName: (result.error as { errorName?: string }).errorName,
            statusCode: (result.error as { statusCode?: number }).statusCode,
          };
        }

        const { cart: updatedCart, failed } = result.data;
        mutate(updatedCart, { revalidate: false });
        // Mirror `addItem` — forward the successful adds to Braze (via GTM).
        const cartLineItems = updatedCart?.lineItems ?? [];
        const addedLineItems = lineItems.filter((li) => !failed.some((f) => f.sku === li.sku));
        pushBrazeAddToCart({
          currency: updatedCart?.sum?.currencyCode ?? '',
          cart_items: mapLineItemsToBrazeProducts(cartLineItems),
          items_added: buildBrazeAddedProducts(cartLineItems, addedLineItems),
        });

        return { ...updatedCart, success: true, failed };
      }),
    [withLock, mutate, businessUnitKey, storeKey],
  );

  /**
   * EWM-942 — parse an uploaded `.xls` / `.xlsx` order file server-side.
   *
   * Deliberately NOT behind `withLock`: this reads a file and returns rows, it
   * never touches the cart. Taking the cart mutation lock would freeze the page
   * (and block a concurrent add) for what is a preview step.
   *
   * Returns a discriminated result rather than throwing, so the drawer can tell
   * an unopenable file (AC-16) from an oversized one (AC-14) from a transport
   * failure, and show the right message for each.
   */
  const quickOrderUploadFile = useCallback(
    async (payload: {
      fileName: string;
      contentBase64: string;
      mapping?: { headerRowIndex: number; skuColumn: number; quantityColumn: number | null };
    }) => {
      const result = await sdk.composableCommerce.cart.quickOrderUploadFile(payload, {
        businessUnitKey: businessUnitKey as string,
        storeKey: storeKey as string,
      });

      if (result.isError) {
        return {
          success: false as const,
          message: result.error.message,
          errorName: (result.error as { errorName?: string }).errorName,
          statusCode: (result.error as { statusCode?: number }).statusCode,
        };
      }

      return { success: true as const, result: result.data };
    },
    [businessUnitKey, storeKey],
  );

  // EWM-610 — quantity updates do NOT take the mutation lock. They are owned by
  // `useOptimisticQuantity` on the consumer (optimistic + trailing debounce + supersede
  // of in-flight writes), so rapid +/- stays interactive and per-item instead of freezing
  // the whole page. The optimistic `mutate(...,{revalidate:false})` and EWM-476 focus
  // revalidation are unchanged. Other content writes (add/remove/discount) raise the
  // 'cart' scope via `withCartLock` → <CartProcessingModal>; checkout/requestQuote/
  // applyFreight opt out with scope:'global' (EWM-612).
  const updateItem = useCallback(
    async (lineItem: { id: string; count: number }) => {
      const payload = { lineItem, businessUnitKey };

      const result = await sdk.composableCommerce.cart.updateItem(payload, {
        businessUnitKey: businessUnitKey as string,
        storeKey: storeKey as string,
      });

      if (!result.isError) {
        mutate(result.data, { revalidate: false });
        notifyIfDiscountAutoRemoved(data, result.data);
      }

      return result.isError
        ? {
            success: false as const,
            errorName: (result.error as { errorName?: string }).errorName,
            statusCode: (result.error as { statusCode?: number }).statusCode,
          }
        : result.data;
    },
    [mutate, businessUnitKey, storeKey, data, notifyIfDiscountAutoRemoved],
  );

  const removeItem = useCallback(
    (lineItemId: string) =>
      withLock(async () => {
        const payload = { lineItem: { id: lineItemId }, businessUnitKey };

        // EWM-583 — capture the line being removed from the pre-remove cart so we can
        // report the removed delta to Braze (it's gone from the result cart).
        const removedLine = data?.lineItems?.find((li) => li.lineItemId === lineItemId);

        const result = await sdk.composableCommerce.cart.removeItem(payload, {
          businessUnitKey: businessUnitKey as string,
          storeKey: storeKey as string,
        });

        mutate();

        if (!result.isError) {
          const cartLineItems = result.data?.lineItems ?? [];
          pushBrazeRemoveFromCart({
            currency: result.data?.sum?.currencyCode ?? removedLine?.price?.currencyCode ?? '',
            cart_items: mapLineItemsToBrazeProducts(cartLineItems),
            items_removed: mapLineItemsToBrazeProducts(removedLine ? [removedLine] : []),
          });
          notifyIfDiscountAutoRemoved(data, result.data);
        }

        return result.isError
          ? {
              success: false as const,
              errorName: (result.error as { errorName?: string }).errorName,
              statusCode: (result.error as { statusCode?: number }).statusCode,
            }
          : result.data;
      }),
    [withLock, mutate, businessUnitKey, storeKey, data, notifyIfDiscountAutoRemoved],
  );

  const requestQuote = useCallback(
    (payload: QuoteRequestPayload) =>
      withLock(
        async () => {
          const result = await sdk.composableCommerce.quote.createQuote(
            { comment: payload.buyerComment, purchaseOrderNumber: payload.purchaseOrderNumber },
            { businessUnitKey: businessUnitKey as string, storeKey: storeKey as string },
          );

          mutate();

          return result.isError ? {} : result.data;
        },
        { scope: 'global' },
      ),
    [withLock, mutate, businessUnitKey, storeKey],
  );

  // EWM-613 — `opts` lets a caller override the lock scope per call site. The cart
  // page keeps the default 'cart' scope (→ <CartProcessingModal>); the checkout page
  // passes { scope: 'global' } so these writes drive section-local pending instead of
  // popping the full-screen modal over the checkout form. Mirrors how checkout /
  // applyFreight already opt out.
  const updateCart = useCallback(
    (
      { shipping, billing, email }: { shipping?: Address; billing?: Address; email?: string },
      opts?: MutationLockOptions,
    ) =>
      withLock(async () => {
        const result = await sdk.composableCommerce.cart.updateCart(
          { shipping, billing, ...(email ? { account: { email } } : {}) },
          { businessUnitKey: businessUnitKey as string, storeKey: storeKey as string },
        );

        if (!result.isError) mutate(result.data, { revalidate: false });

        return result.isError ? { success: false, error: result.error } : { ...result.data, success: true };
      }, opts),
    [withLock, mutate, businessUnitKey, storeKey],
  );

  const redeemDiscount = useCallback(
    (code: string, opts?: MutationLockOptions) =>
      withLock(async () => {
        const result = await sdk.composableCommerce.cart.redeemDiscountCode(
          { code },
          { businessUnitKey: businessUnitKey as string, storeKey: storeKey as string },
        );

        if (result.isError) {
          return {
            success: false,
            message: result.error.message ?? '',
            errorName: (result.error as { errorName?: string }).errorName,
            statusCode: (result.error as { statusCode?: number }).statusCode,
          };
        }

        mutate(result.data, { revalidate: false });

        return { success: true };
      }, opts),
    [withLock, mutate, businessUnitKey, storeKey],
  );

  const removeDiscount = useCallback(
    (discountCodeId: string, opts?: MutationLockOptions) =>
      withLock(async () => {
        const result = await sdk.composableCommerce.cart.removeDiscountCode(
          { discountCodeId },
          { businessUnitKey: businessUnitKey as string, storeKey: storeKey as string },
        );

        if (!result.isError) mutate(result.data, { revalidate: false });

        return result.isError
          ? ({
              success: false as const,
              errorName: (result.error as { errorName?: string }).errorName,
              statusCode: (result.error as { statusCode?: number }).statusCode,
            } as Partial<Cart>)
          : result.data;
      }, opts),
    [withLock, mutate, businessUnitKey, storeKey],
  );

  // EWM-929 — "Empty cart". The BFF retires the cart (delete, or freeze when it
  // carries payments) and can legitimately refuse: a checkout lock (409), a
  // missing associate permission (403), an upstream failure. The old body
  // awaited the call, discarded the result and `mutate()`d unconditionally, so
  // a refusal still read as success — the page blanked while commercetools kept
  // the cart, and it reappeared on the next navigation (the reported defect).
  //
  // Now it returns the sibling mutation shape and only commits — revalidate +
  // analytics — once the BFF has confirmed. The lines are snapshotted BEFORE
  // the call because a successful clear leaves nothing to report.
  const clearCart = useCallback(
    () =>
      withLock(async (): Promise<ClearCartResult> => {
        const clearedLineItems = data?.lineItems ?? [];
        const currency = data?.sum?.currencyCode ?? '';

        const result = await sdk.composableCommerce.cart.clearCart();

        if (result.isError) {
          const error = result.error as { errorName?: string; statusCode?: number; message?: string } | undefined;
          return {
            success: false,
            errorName: error?.errorName,
            statusCode: error?.statusCode,
            message: error?.message,
          };
        }

        mutate();

        // EWM-929 AC-7 — the GA4 `cart_cleared` event is deliberately NOT emitted here.
        // It has to report the per-unit price the customer was actually looking at (the
        // EWM-523 live-price overlay), and only the cart organism holds those mapped
        // lines; this hook sees commercetools' add-time price, which measured $12.40 on
        // screen against 14.53 in the event on an NZ line. Its sibling `remove_from_cart`
        // is fired from the cart table for exactly the same reason, so the two events
        // now agree. Braze stays here: its events mirror the cart entity rather than the
        // rendered page, matching `pushBrazeAddToCart` above.

        pushBrazeRemoveFromCart({
          currency,
          cart_items: [],
          items_removed: mapLineItemsToBrazeProducts(clearedLineItems),
        });

        return { success: true };
      }),
    [withLock, mutate, data],
  );

  const checkout = useCallback(
    ({
      purchaseOrderNumber,
      paymentState,
      shipmentState,
      paymentMethod,
      rushOrder,
      windcaveSessionId,
      idempotencyKey,
    }: CheckoutPayload) =>
      withLock(
        async () => {
          const result = await sdk.composableCommerce.cart.checkout(
            {
              purchaseOrderNumber,
              paymentState,
              shipmentState,
              paymentMethod,
              rushOrder,
              windcaveSessionId,
              idempotencyKey,
            },
            { businessUnitKey: businessUnitKey as string, storeKey: storeKey as string },
          );

          mutate();

          // EWM-355 — Surface the BFF errorName on failure so callers can
          // distinguish business-specific errors (e.g. payment-amount
          // mismatch → show retry banner) from generic transport failures
          // (→ FPRN safety-net processing). Backward-compat: on success the
          // resolved value still has `order` containing the Partial<Order>,
          // and `errorName === undefined`.
          if (result.isError) {
            // EWM-353 — the BFF error body is FLAT: handleError serializes
            // { statusCode, message, errorName } at the top level, so errorName is on
            // `result.error` directly — NOT `result.error.body`. Reading `.body.errorName`
            // made errorName always undefined, so the wrapper's
            // `order_creation_failed_refunded_error` branch never fired → it fell
            // through to goToProcessing + setIsFinalizingOrder(false) → the checkout
            // empty-cart guard bounced the customer to /cart. (Confirmed against the
            // live error shape: { statusCode: 422, errorName: 'order_creation_failed_refunded_error' }.)
            const err = result.error as { errorName?: string; statusCode?: number } | undefined;
            return {
              order: {} as Partial<Order>,
              errorName: err?.errorName,
              statusCode: err?.statusCode,
            };
          }
          // EWM-634 — best-effort: the order just decremented stock for these
          // SKUs, so bust their `ct-stock` cache → PLP/PDP show fresh availability
          // before the 60s TTL would expire. Fire-and-forget: NEVER block or fail
          // checkout on it (the TTL is the backstop if the request is dropped on
          // navigation to /thank-you). `data` is the just-ordered cart snapshot.
          const orderedSkus = (data?.lineItems ?? [])
            .map((li) => li.variant?.sku)
            .filter((sku): sku is string => typeof sku === 'string' && sku.length > 0);
          if (orderedSkus.length > 0) void revalidateStockForSkus(orderedSkus).catch(() => {});

          return { order: result.data as Partial<Order>, errorName: undefined, statusCode: undefined };
        },
        { scope: 'global' },
      ),
    [withLock, mutate, businessUnitKey, storeKey, data],
  );

  // EWM-644 — removed the eager getShippingMethods SWR. It fired cart/getShippingMethods
  // on every useCart mount (i.e. every page, via the header) but nothing read the result —
  // checkout feeds its shipping step from cart.availableShippingMethods. Dead fetch removed.

  const setShippingMethod = useCallback(
    (shippingMethodId: string) =>
      withLock(async () => {
        const result = await sdk.composableCommerce.cart.setShippingMethod(
          { shippingMethod: { id: shippingMethodId } },
          { businessUnitKey: businessUnitKey as string, storeKey: storeKey as string },
        );

        if (!result.isError) mutate(result.data, { revalidate: false });

        return result.isError ? ({} as Partial<Cart>) : result.data;
      }),
    [withLock, businessUnitKey, storeKey, mutate],
  );

  // GST-on-top follow-up — apply freight to the cart server-side and update
  // the local cart with the result. After this, commercetools has computed
  // the cart's full taxedPrice (line items + freight + GST on both), so
  // `cart.transaction` is the authoritative, CT-backed total — no FE-side
  // freight-tax estimate needed. The checkout page calls this on load and on
  // any address / line-item / rush change. Returns the updated cart (or
  // undefined on error; the caller keeps the prior cart + can retry).
  const applyFreight = useCallback(
    (rushOrder: boolean): Promise<Cart | undefined> =>
      withLock(
        async () => {
          const result = await sdk.callAction<Cart>({
            actionName: 'freight/applyToCart',
            payload: { rushOrder },
          });
          if (result.isError) return undefined;
          const updated = result.data as Cart;
          mutate(updated, { revalidate: false });
          return updated;
        },
        { scope: 'global' },
      ),
    [withLock, mutate],
  );

  // EWM-740 — force a CT recalculation so the cart reflects current promotion
  // validity. CT only re-evaluates discounts on a write / this action / order
  // creation (never on a passive read), so an automatic Cart Discount that expired
  // or was deactivated while the cart sat idle would otherwise keep showing. The
  // checkout page calls this once on entry so the review totals match what will
  // actually be charged. scope:'global' → no CartProcessingModal.
  const recalculate = useCallback(
    (): Promise<Cart | undefined> =>
      withLock(
        async () => {
          const result = await sdk.callAction<Cart>({
            actionName: 'cart/recalculate',
            payload: {},
          });
          if (result.isError) return undefined;
          const updated = result.data as Cart;
          mutate(updated, { revalidate: false });
          notifyIfDiscountAutoRemoved(data, updated);
          return updated;
        },
        { scope: 'global' },
      ),
    [withLock, mutate, notifyIfDiscountAutoRemoved, data],
  );

  return {
    cart: data ? { ...data, transaction: calculateTransaction(data) } : undefined,
    mutateAll,
    refreshCart,
    isLoading,
    isQuotationCart,
    totalItems: data?.lineItems?.reduce((acc, curr) => acc + (curr.count ?? 1), 0) ?? 0,
    // EWM-441 — distinct line-item count (not the summed quantity above). The
    // cart icon badge shows this so B2B carts with large per-line quantities
    // don't inflate the badge. `totalItems` stays the quantity sum for analytics.
    totalLineItems: data?.lineItems?.length ?? 0,
    addItem,
    quickOrderAddItems,
    quickOrderUploadFile,
    updateItem,
    removeItem,
    requestQuote,
    updateCart,
    setShippingMethod,
    applyFreight,
    recalculate,
    redeemDiscount,
    removeDiscount,
    checkout,
    clearCart,
  };
};

export default useCart;
